import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTheme } from "styled-components";
import { Button, Text } from "../../../components/common";
import {
  Buttons,
  ContentContainer,
  OnboardingDescription,
  OnboardingTitle,
} from "../../../components/common/onboarding/index.styled";
import RadioImgInputComponent from "../../../components/onboarding/radio-img-input";
import RadioInput from "../../../components/onboarding/radio-input";
import useRootTranslation from "../../../hooks/use-root-translation";
import { AppState } from "../../../store/app-state";
import {
  fetchCandidateCareer
} from "../../../store/candidate/candidate-actions";
import { setupCompleted } from "../../../store/sign-in/sign-in-actions";
import OnboardingLayout from "./components/onboarding-layout";
import { Radios, RadiosImage } from "./onboarding-page.styled";
import { Career } from "../../../models/career.model";
import { convertIdNameToLabelValue } from "../../../utils/redux/interface-converter";

const OnboardingPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { root } = useRootTranslation("onboarding", "private-home-page");
  const { data: careers } = useSelector(
    (state: AppState) => state.candidateState.candidateCareerData
  );
  const user = useSelector((state: AppState) => state.authState.user);
  const [skill, setSkill] = useState<string>("");
  const [experience, setExperience] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [career, setCareer] = useState<Career>();
  const history = useHistory();

  const { data: experienceTimeData } = useSelector(
    (state: AppState) => state.commonState.experienceTimeData
  );

  const experienceTimeOptions = useMemo(() => {
    return experienceTimeData.map((e) => convertIdNameToLabelValue(e));
  }, [experienceTimeData]);

  const next = useCallback(() => {
    if (user) {
      dispatch(setupCompleted(user));
      setLoading(true);
      setTimeout(() => {
        history.push(
          `/ranking?ecosystem=${skill}&experience=${encodeURIComponent(
            experience
          )}&onboarding=true&career=${career?.id}&currency=${user?.corporate?.currency ?? 'BRL'}`
        );
        setLoading(false);
      }, 1000);
    }

  }, [dispatch, history, experience, skill, user, career, setLoading]);

  useEffect(() => {
    dispatch(fetchCandidateCareer());
  }, [dispatch]);

  return (
    <OnboardingLayout>
      <ContentContainer>
        <OnboardingTitle color={theme.colors.primaryBlue.main}>
          {root("title")}
        </OnboardingTitle>
        <OnboardingDescription
          color={theme.colors.gray900.main}
          margin="1rem 0"
        >
          {root("sub-title")}
        </OnboardingDescription>
        <Text color={theme.colors.gray900.main} margin="1rem 0">
          {root("main-career")}
        </Text>
        <Radios>
          {careers?.map((exp, index) => (
            <RadioInput
              id={`career-${index}`}
              radioGroup="career"
              label={exp.name}
              value={exp.id}
              setChecked={(value) => {
                if (value) {
                  setCareer(exp);
                  setSkill("");
                }
              }}
            />
          ))}
        </Radios>

        {career && (
          <>
            <Text color={theme.colors.gray900.main} margin="1rem 0">
              {root("main-skill")}
            </Text>
            <RadiosImage>
              {career?.ecosystems?.map((ecosystem, index) => (
                <RadioImgInputComponent
                  checked={skill === ecosystem.id}
                  id={`skill-${index}`}
                  radioGroup="main-skill"
                  label={ecosystem.name}
                  value={ecosystem.id}
                  setChecked={(value) => {
                    if (value) {
                      setSkill(ecosystem.id);
                    }
                  }}
                  img={ecosystem.icon_url}
                />
              ))}
            </RadiosImage>
          </>
        )}

        <Text color={theme.colors.gray900.main} margin="1rem 0">
          {root("experience")}
        </Text>
        <Radios>
          {experienceTimeOptions?.map((exp, index) => (
            <RadioInput
              id={`exp-${index}`}
              radioGroup="experience"
              label={exp.label}
              value={exp.value}
              setChecked={(value) => {
                if (value) {
                  setExperience(exp.value);
                }
              }}
            />
          ))}
        </Radios>
        <Buttons>
          <Button
            textColor={theme.colors.primaryBlue.text}
            backgroundColor={theme.colors.primaryBlue.main}
            width="fit-content"
            onClick={() => next()}
            disabled={!skill || !experience || loading || !career}
          >
            {root("button")}
          </Button>
        </Buttons>
      </ContentContainer>
    </OnboardingLayout>
  );
};

export default OnboardingPage;
